import { useState, useEffect } from "react";
import axios from "axios";
import { format, parseISO } from "date-fns";
import { 
  Plus, BookOpen, Zap, Brain, ChevronRight, X, Filter,
  Tag, BarChart3, TrendingUp, Lightbulb
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// State colors and info - Updated for readability on dark backgrounds
const STATES = {
  Ground: { color: "#4CAF82", bg: "bg-[rgba(76,175,130,0.09)]", border: "border-[#4CAF82]/50", text: "text-[#4CAF82]" },
  Observe: { color: "#4A90C4", bg: "bg-[rgba(74,144,196,0.09)]", border: "border-[#4A90C4]/50", text: "text-[#4A90C4]" },
  Connect: { color: "#B06BB0", bg: "bg-[rgba(176,107,176,0.09)]", border: "border-[#B06BB0]/50", text: "text-[#B06BB0]" },
  Execute: { color: "#D4723A", bg: "bg-[rgba(212,114,58,0.09)]", border: "border-[#D4723A]/50", text: "text-[#D4723A]" },
  Integrate: { color: "#7BAF4A", bg: "bg-[rgba(123,175,74,0.09)]", border: "border-[#7BAF4A]/50", text: "text-[#7BAF4A]" }
};

// Journal prompts
const PROMPTS = [
  { id: "post_protocol", label: "Post-Protocol Reflection", prompt: "How did the protocol feel today? What state did you find yourself in most?" },
  { id: "challenge", label: "Daily Challenge", prompt: "What challenge did you face today? Which state would have served you best?" },
  { id: "insight", label: "State Insight", prompt: "What insight came up about your default state patterns?" },
  { id: "free", label: "Free Write", prompt: "" }
];

// State tag component - use span for readonly display
const StateTag = ({ state, selected, onClick, size = "md", readonly = false }) => {
  const info = STATES[state];
  const sizeClasses = size === "sm" ? "px-2 py-1 text-xs" : "px-3 py-1.5 text-sm";
  
  const baseClasses = `rounded-full font-medium transition-all ${sizeClasses} ${
    selected 
      ? `${info.bg} ${info.text} ${info.border} border` 
      : "bg-clarity-subtle/50 text-slate-500 hover:text-slate-300 border border-transparent"
  }`;
  
  if (readonly) {
    return <span className={baseClasses}>{state}</span>;
  }
  
  return (
    <button
      type="button"
      onClick={onClick}
      className={baseClasses}
    >
      {state}
    </button>
  );
};

// State stats visualization
const StateStats = ({ stats }) => {
  if (!stats) return null;
  
  const total = Object.values(stats.state_counts).reduce((a, b) => a + b, 0);
  
  return (
    <Card className="bg-clarity-paper/50 border-white/10 mb-6">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-slate-400 flex items-center gap-2">
          <BarChart3 className="w-4 h-4" />
          Your State Pattern
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-1 mb-4">
          {Object.entries(stats.state_counts).map(([state, count]) => {
            const percentage = total > 0 ? (count / total) * 100 : 0;
            const info = STATES[state];
            return (
              <div 
                key={state}
                className="h-3 rounded-full transition-all"
                style={{ 
                  width: `${Math.max(percentage, 2)}%`, 
                  backgroundColor: info?.color || '#666',
                  minWidth: count > 0 ? '8px' : '4px'
                }}
                title={`${state}: ${count} entries (${percentage.toFixed(0)}%)`}
              />
            );
          })}
        </div>
        
        <div className="flex flex-wrap gap-3 text-xs">
          {Object.entries(stats.state_counts).map(([state, count]) => {
            const info = STATES[state];
            return (
              <div key={state} className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: info?.color }} />
                <span className="text-slate-400">{state}: {count}</span>
              </div>
            );
          })}
        </div>
        
        {stats.dominant_state && (
          <div className="mt-4 pt-4 border-t border-white/5">
            <div className="flex items-center gap-2 text-sm">
              <TrendingUp className="w-4 h-4 text-brand-secondary" />
              <span className="text-slate-400">Dominant state:</span>
              <span className="text-white font-medium">{stats.dominant_state}</span>
            </div>
            {stats.missing_states?.length > 0 && (
              <div className="flex items-center gap-2 text-sm mt-2">
                <Lightbulb className="w-4 h-4 text-amber-500" />
                <span className="text-slate-400">Unexplored:</span>
                <span className="text-amber-500">{stats.missing_states.join(", ")}</span>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const Reflection = ({ user }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewEntry, setShowNewEntry] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [activeFilter, setActiveFilter] = useState(null);
  const [stats, setStats] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    how_did_it_feel: "",
    insights: "",
    energy_level: 3,
    clarity_level: 3,
    state_tags: []
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchEntries();
    fetchStats();
  }, [activeFilter]);

  const fetchEntries = async () => {
    try {
      let url = `${API}/journal`;
      if (activeFilter) {
        url += `?state_filter=${activeFilter}`;
      }
      const response = await axios.get(url, { withCredentials: true });
      setEntries(response.data);
    } catch (error) {
      console.error("Error fetching entries:", error);
      toast.error("Failed to load reflections");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/journal/stats/states`, { withCredentials: true });
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const handleSubmit = async () => {
    if (!formData.how_did_it_feel.trim() && !formData.insights.trim()) {
      toast.error("Please add some content to your reflection");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/journal`, {
        ...formData,
        prompt_used: selectedPrompt?.id || "free"
      }, { withCredentials: true });
      
      toast.success("Reflection saved");
      setShowNewEntry(false);
      setFormData({
        how_did_it_feel: "",
        insights: "",
        energy_level: 3,
        clarity_level: 3,
        state_tags: []
      });
      setSelectedPrompt(null);
      fetchEntries();
      fetchStats();
    } catch (error) {
      console.error("Error saving entry:", error);
      toast.error("Failed to save reflection");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleStateTag = (state) => {
    setFormData(prev => ({
      ...prev,
      state_tags: prev.state_tags.includes(state)
        ? prev.state_tags.filter(s => s !== state)
        : [...prev.state_tags, state]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-12 h-12 border-4 border-brand-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reflection-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 
            className="text-3xl font-bold text-white mb-2"
            style={{ fontFamily: "'DM Serif Display', serif" }}
          >
            Reflection
          </h1>
          <p className="text-slate-400 text-sm">
            Capture insights and track your state patterns over time.
          </p>
        </div>
        <Button 
          onClick={() => setShowNewEntry(true)}
          className="bg-amber-600 hover:bg-amber-700 text-white"
          data-testid="new-reflection-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Entry
        </Button>
      </div>

      {/* State Stats */}
      <StateStats stats={stats} />

      {/* State Filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="w-4 h-4 text-slate-500" />
        <button
          onClick={() => setActiveFilter(null)}
          className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
            !activeFilter 
              ? "bg-brand-primary/20 text-brand-primary border border-brand-primary/50" 
              : "bg-clarity-subtle text-slate-400 hover:text-white"
          }`}
        >
          All
        </button>
        {Object.keys(STATES).map(state => (
          <button
            key={state}
            onClick={() => setActiveFilter(activeFilter === state ? null : state)}
            className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
              activeFilter === state 
                ? `${STATES[state].bg} ${STATES[state].text} border ${STATES[state].border}` 
                : "bg-clarity-subtle text-slate-400 hover:text-white"
            }`}
          >
            {state}
          </button>
        ))}
      </div>

      {/* Entries List */}
      <div className="space-y-4">
        {entries.length === 0 ? (
          <Card className="bg-clarity-paper border-white/10">
            <CardContent className="py-12 text-center">
              <BookOpen className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">No reflections yet</h3>
              <p className="text-slate-400 text-sm mb-4">
                {activeFilter 
                  ? `No entries tagged with "${activeFilter}" state`
                  : "Start capturing your insights after each protocol session"
                }
              </p>
              {!activeFilter && (
                <Button 
                  onClick={() => setShowNewEntry(true)}
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  Write Your First Reflection
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          entries.map((entry) => (
            <Card 
              key={entry.entry_id} 
              className="bg-clarity-paper border-white/10 hover:border-white/20 transition-colors"
            >
              <CardContent className="py-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <BookOpen className="w-4 h-4 text-brand-secondary" />
                    <span className="text-sm text-slate-400">
                      {format(parseISO(entry.created_at), "MMM d, yyyy 'at' h:mm a")}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500 flex items-center gap-1">
                      <Zap className="w-3 h-3" /> {entry.energy_level}/5
                    </span>
                    <span className="text-xs text-slate-500 flex items-center gap-1">
                      <Brain className="w-3 h-3" /> {entry.clarity_level}/5
                    </span>
                  </div>
                </div>
                
                {entry.how_did_it_feel && (
                  <p className="text-white mb-2">{entry.how_did_it_feel}</p>
                )}
                {entry.insights && (
                  <p className="text-slate-400 text-sm">{entry.insights}</p>
                )}
                
                {entry.state_tags?.length > 0 && (
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/5">
                    <Tag className="w-3 h-3 text-slate-500" />
                    <div className="flex gap-1.5 flex-wrap">
                      {entry.state_tags.map(state => (
                        <StateTag key={state} state={state} selected size="sm" readonly />
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* New Entry Dialog */}
      <Dialog open={showNewEntry} onOpenChange={setShowNewEntry}>
        <DialogContent className="bg-clarity-paper border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl text-white flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-brand-secondary" />
              New Reflection
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            {/* Prompt Selection */}
            {!selectedPrompt && (
              <div className="space-y-2">
                <Label className="text-slate-400">Choose a prompt or free write</Label>
                <div className="grid grid-cols-1 gap-2">
                  {PROMPTS.map(prompt => (
                    <button
                      key={prompt.id}
                      onClick={() => setSelectedPrompt(prompt)}
                      className="p-3 text-left rounded-lg bg-clarity-subtle hover:bg-clarity-subtle/80 border border-white/5 hover:border-white/10 transition-colors"
                    >
                      <span className="text-white text-sm">{prompt.label}</span>
                      {prompt.prompt && (
                        <p className="text-slate-500 text-xs mt-1">{prompt.prompt}</p>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {selectedPrompt && (
              <>
                {/* Prompt Header */}
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm text-brand-secondary">{selectedPrompt.label}</span>
                    {selectedPrompt.prompt && (
                      <p className="text-slate-400 text-sm mt-1">{selectedPrompt.prompt}</p>
                    )}
                  </div>
                  <button
                    onClick={() => setSelectedPrompt(null)}
                    className="text-slate-500 hover:text-white"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {/* Main Content */}
                <div>
                  <Label className="text-slate-400">How did it feel?</Label>
                  <Textarea
                    value={formData.how_did_it_feel}
                    onChange={(e) => setFormData({...formData, how_did_it_feel: e.target.value})}
                    placeholder="Describe your experience..."
                    className="mt-2 bg-clarity-subtle border-white/10 text-white placeholder:text-slate-500 min-h-[100px]"
                  />
                </div>

                {/* Insights */}
                <div>
                  <Label className="text-slate-400">Key insights</Label>
                  <Textarea
                    value={formData.insights}
                    onChange={(e) => setFormData({...formData, insights: e.target.value})}
                    placeholder="What did you learn or notice?"
                    className="mt-2 bg-clarity-subtle border-white/10 text-white placeholder:text-slate-500"
                  />
                </div>

                {/* State Tags */}
                <div>
                  <Label className="text-slate-400 mb-2 block">Tag states experienced</Label>
                  <div className="flex flex-wrap gap-2">
                    {Object.keys(STATES).map(state => (
                      <StateTag
                        key={state}
                        state={state}
                        selected={formData.state_tags.includes(state)}
                        onClick={() => toggleStateTag(state)}
                      />
                    ))}
                  </div>
                </div>

                {/* Energy & Clarity */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-400 flex items-center gap-1">
                      <Zap className="w-3 h-3" /> Energy Level
                    </Label>
                    <div className="flex gap-1 mt-2">
                      {[1, 2, 3, 4, 5].map(level => (
                        <button
                          key={level}
                          onClick={() => setFormData({...formData, energy_level: level})}
                          className={`w-8 h-8 rounded ${
                            formData.energy_level >= level 
                              ? "bg-amber-500 text-white" 
                              : "bg-clarity-subtle text-slate-500"
                          }`}
                        >
                          {level}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="text-slate-400 flex items-center gap-1">
                      <Brain className="w-3 h-3" /> Clarity Level
                    </Label>
                    <div className="flex gap-1 mt-2">
                      {[1, 2, 3, 4, 5].map(level => (
                        <button
                          key={level}
                          onClick={() => setFormData({...formData, clarity_level: level})}
                          className={`w-8 h-8 rounded ${
                            formData.clarity_level >= level 
                              ? "bg-blue-500 text-white" 
                              : "bg-clarity-subtle text-slate-500"
                          }`}
                        >
                          {level}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Submit */}
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="save-reflection-btn"
                >
                  {submitting ? "Saving..." : "Save Reflection"}
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
